import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, Phone, Mail, ArrowUpRight, MapPin } from "lucide-react";
import { company, navItems } from "../mock";
import { Sheet, SheetContent, SheetTrigger, SheetClose, SheetTitle, SheetDescription } from "./ui/sheet";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const isActive = (to) =>
    to === "/" ? location.pathname === "/" : location.pathname.startsWith(to);

  return (
    <header className="sticky top-0 z-50" data-testid="site-header">
      {/* Utility bar */}
      <div className="hidden lg:block bg-brand-ink text-slate-400">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex items-center justify-between h-10 font-mono text-[11px] tracking-[0.08em]">
          <div className="flex items-center gap-8">
            <a href={company.phoneHref} data-testid="topbar-phone" className="flex items-center gap-2 hover:text-white transition-colors duration-300">
              <Phone className="w-3 h-3 text-brand-glow" />{company.phone}
            </a>
            <a href={`mailto:${company.email}`} data-testid="topbar-email" className="flex items-center gap-2 hover:text-white transition-colors duration-300">
              <Mail className="w-3 h-3 text-brand-glow" />{company.email}
            </a>
          </div>
          <div className="flex items-center gap-6">
            <span className="flex items-center gap-2"><MapPin className="w-3 h-3 text-brand-glow" />{company.location}</span>
            <span className="h-3 w-px bg-white/15" />
            <span className="text-slate-500">EN 1090-2 · AWS · TSE</span>
            <span className="h-3 w-px bg-white/15" />
            <span>{company.hoursWeek}</span>
          </div>
        </div>
      </div>

      {/* Main nav */}
      <div
        className={`bg-white/85 backdrop-blur-2xl border-b transition-[box-shadow,border-color,background-color] duration-500 ${
          scrolled
            ? "border-brand-line shadow-[0_1px_30px_rgba(12,19,34,0.07)]"
            : "border-brand-line/60"
        }`}
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex items-center justify-between h-[78px] lg:h-[92px]">
          <Link to="/" className="flex items-center gap-4 group" data-testid="navbar-logo-link">
            <img
              src={company.logo}
              alt="İlka Mühendislik"
              className="h-14 lg:h-[68px] w-auto transition-transform duration-500 group-hover:scale-[1.04]"
            />
            <span className="h-10 sm:h-11 w-px bg-brand-line" />
            <div className="leading-none">
              <div className="font-display text-[15px] sm:text-[20px] lg:text-[23px] font-semibold tracking-[-0.03em] text-brand-ink">
                İLKA <span className="font-light text-brand-accent">MÜHENDİSLİK</span>
              </div>
              <div className="mt-2 font-mono text-[8px] sm:text-[9.5px] uppercase tracking-[0.24em] sm:tracking-[0.34em] text-brand-muted">
                {company.tagline}
              </div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
            {navItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                data-testid={`nav-link-${item.to === "/" ? "home" : item.to.replace("/", "")}`}
                className={`relative font-heading text-[12.5px] font-medium uppercase tracking-[0.1em] px-3.5 py-2 transition-colors duration-300 ${
                  isActive(item.to) ? "text-brand-accent" : "text-brand-steel hover:text-brand-accent"
                }`}
              >
                {item.label}
                <span
                  className={`absolute left-3.5 right-3.5 -bottom-1 h-px bg-brand-accent origin-left transition-transform duration-500 ${
                    isActive(item.to) ? "scale-x-100" : "scale-x-0"
                  }`}
                />
              </Link>
            ))}
            <Link
              to="/teklif-iste"
              data-testid="navbar-quote-btn"
              className="ml-5 group font-mono text-[11px] font-medium uppercase tracking-[0.16em] bg-brand-accent hover:bg-brand-accent-dark text-white pl-5 pr-3 py-3 rounded-full flex items-center gap-2.5 transition-colors duration-300 shadow-[0_10px_26px_-14px_rgba(15,56,122,0.85)]"
            >
              Teklif İste
              <span className="w-6 h-6 rounded-full bg-white/15 flex items-center justify-center transition-transform duration-300 group-hover:rotate-45">
                <ArrowUpRight className="w-3 h-3" />
              </span>
            </Link>
          </nav>

          {/* Mobile */}
          <div className="lg:hidden">
            <Sheet>
              <SheetTrigger aria-label="Menü" data-testid="mobile-menu-trigger" className="p-2 text-brand-ink">
                <Menu className="w-6 h-6" />
              </SheetTrigger>
              <SheetContent side="right" className="bg-white w-[310px] p-0 border-brand-line">
                <SheetTitle className="sr-only">Menü</SheetTitle>
                <SheetDescription className="sr-only">Site menüsü ve iletişim bilgileri</SheetDescription>
                <div className="p-6 border-b border-brand-line flex items-center gap-3">
                  <img src={company.logo} alt="logo" className="h-12" />
                  <div>
                    <div className="font-display font-semibold text-brand-ink text-base leading-none tracking-tight">İLKA MÜHENDİSLİK</div>
                    <div className="font-mono text-[9px] uppercase tracking-[0.26em] text-brand-muted mt-2">{company.tagline}</div>
                  </div>
                </div>
                <nav className="flex flex-col py-2">
                  {navItems.map((item) => (
                    <SheetClose asChild key={item.to}>
                      <Link
                        to={item.to}
                        data-testid={`mobile-nav-link-${item.to === "/" ? "home" : item.to.replace("/", "")}`}
                        className={`font-heading text-[13px] uppercase tracking-[0.1em] px-6 py-4 border-b border-brand-line/70 flex items-center justify-between ${
                          isActive(item.to) ? "text-brand-accent" : "text-brand-ink"
                        }`}
                      >
                        {item.label}
                        <ArrowUpRight className="w-4 h-4 opacity-40" />
                      </Link>
                    </SheetClose>
                  ))}
                  <SheetClose asChild>
                    <Link
                      to="/teklif-iste"
                      data-testid="mobile-quote-btn"
                      className="m-6 font-mono text-[11px] uppercase tracking-[0.16em] bg-brand-accent text-white px-5 py-3.5 rounded-full text-center"
                    >
                      Teklif İste
                    </Link>
                  </SheetClose>
                  <div className="px-6 pb-6 text-[13px] text-brand-muted space-y-2.5">
                    <a href={company.phoneHref} className="flex items-center gap-2"><Phone className="w-4 h-4 text-brand-accent" />{company.phone}</a>
                    <a href={`mailto:${company.email}`} className="flex items-center gap-2 break-all"><Mail className="w-4 h-4 text-brand-accent" />{company.email}</a>
                  </div>
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
}
